import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { collection, doc, writeBatch } from "firebase/firestore";
import { ref, set as setRealtimeData } from "firebase/database";
import { LoaderCircle, X } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { db, realtimeDb } from "../firebase/config";
const TEAM_LEVELS = [
    { value: "school", label: "Primary or Secondary School Team" },
    { value: "college", label: "College Team" },
    { value: "youth", label: "Club Team (Youth)" },
    { value: "open", label: "Club Team (Open)" },
];
const TEAM_GENDERS = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
];
const TEAM_UNITS = [
    { value: "imperial", label: "Feet" },
    { value: "metric", label: "Meters" },
];
function fallbackLocation(postalCode) {
    return {
        postalCode,
        city: "",
        state: "",
        county: "",
        country: "US",
    };
}
function zipCodeValue(value) {
    return value.replace(/\D/g, "").slice(0, 5);
}
async function lookupZipCode(postalCode) {
    const baseUrl = process.env.ZIP_CODE_BASE_URL;
    if (!baseUrl)
        return fallbackLocation(postalCode);
    try {
        const response = await fetch(`${baseUrl}${encodeURIComponent(postalCode)}`);
        if (!response.ok)
            return fallbackLocation(postalCode);
        const data = await response.json();
        const results = data.results;
        const postalResults = results?.[postalCode];
        const entry = (Array.isArray(postalResults) ? postalResults[0] : postalResults);
        if (!entry)
            return fallbackLocation(postalCode);
        return {
            postalCode: String(entry.postal_code ?? postalCode),
            city: String(entry.city ?? entry.place_name ?? ""),
            county: String(entry.province ?? entry.county ?? ""),
            state: String(entry.state_code ?? entry.state ?? ""),
            country: "US",
        };
    }
    catch {
        return fallbackLocation(postalCode);
    }
}
export function AddTeamDialog({ open, onClose, onTeamCreated }) {
    const { profile, user } = useAuth();
    const [level, setLevel] = useState("school");
    const [name, setName] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [gender, setGender] = useState("female");
    const [unit, setUnit] = useState("imperial");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const schoolLevel = level === "school" || level === "college";
    const namePlaceholder = schoolLevel ? "School name" : "Team or club name";
    const ownerName = useMemo(() => {
        const candidate = profile?.name?.trim() || user?.displayName?.trim() || profile?.email || user?.email || "";
        return candidate;
    }, [profile?.email, profile?.name, user?.displayName, user?.email]);
    if (!open)
        return null;
    function resetAndClose() {
        if (saving)
            return;
        setLevel("school");
        setName("");
        setZipCode("");
        setGender("female");
        setUnit("imperial");
        setError("");
        onClose();
    }
    async function createTeam(event) {
        event.preventDefault();
        if (!user)
            return;
        const normalizedName = name.trim();
        if (!normalizedName) {
            setError(`${namePlaceholder} is required.`);
            return;
        }
        if (!/^\d{5}$/.test(zipCode)) {
            setError("Enter a valid 5 digit US zip code.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const location = await lookupZipCode(zipCode);
            const teamRef = doc(collection(db, "teams"));
            const owner = {
                id: user.uid,
                name: ownerName,
                email: profile?.email || user.email || "",
            };
            const team = {
                gender,
                id: teamRef.id,
                level,
                location,
                managers: [user.uid],
                name: normalizedName,
                nameLower: normalizedName.toLowerCase(),
                owner,
                public: false,
                unit,
                slug: "",
                seasons: {},
            };
            const batch = writeBatch(db);
            batch.set(teamRef, team);
            batch.update(doc(db, "users", user.uid), { [`teams.${teamRef.id}`]: "owner" });
            await batch.commit();
            await setRealtimeData(ref(realtimeDb, `access/${teamRef.id}/owner`), user.uid);
            onTeamCreated?.(teamRef.id);
            resetAndClose();
        }
        catch (caughtError) {
            setError(caughtError instanceof Error ? caughtError.message : "Team could not be created.");
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "add-team-backdrop", role: "presentation", onMouseDown: resetAndClose, children: _jsxs("section", { className: "add-team-dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "add-team-title", onMouseDown: (event) => event.stopPropagation(), children: [_jsxs("header", { children: [_jsx("span", { children: _jsx("img", { src: "/img/logo.png", alt: "" }) }), _jsxs("div", { children: [_jsx("h2", { id: "add-team-title", children: "New Team" }), _jsx("p", { children: "Create a team and start a new season." })] }), _jsx("button", { type: "button", disabled: saving, onClick: resetAndClose, "aria-label": "Close", children: _jsx(X, { size: 20 }) })] }), _jsxs("form", { onSubmit: createTeam, children: [_jsxs("label", { children: [_jsx("span", { children: "Level" }), _jsx("select", { value: level, onChange: (event) => setLevel(event.target.value), disabled: saving, children: TEAM_LEVELS.map((option) => _jsx("option", { value: option.value, children: option.label }, option.value)) })] }), _jsxs("label", { children: [_jsxs("span", { children: [namePlaceholder, _jsx("em", { children: "*" })] }), _jsx("input", { value: name, onChange: (event) => setName(event.target.value), placeholder: namePlaceholder, disabled: saving, minLength: 3, maxLength: 35 })] }), _jsxs("label", { children: [_jsxs("span", { children: ["Zip Code", _jsx("em", { children: "*" })] }), _jsx("input", { value: zipCode, onChange: (event) => setZipCode(zipCodeValue(event.target.value)), placeholder: "5 digit zip code", disabled: saving, inputMode: "numeric", maxLength: 5 })] }), _jsxs("div", { className: "add-team-field-row", children: [_jsxs("label", { children: [_jsx("span", { children: "Gender" }), _jsx("select", { value: gender, onChange: (event) => setGender(event.target.value), disabled: saving, children: TEAM_GENDERS.map((option) => _jsx("option", { value: option.value, children: option.label }, option.value)) })] }), _jsxs("label", { children: [_jsx("span", { children: "Field Event Units" }), _jsx("select", { value: unit, onChange: (event) => setUnit(event.target.value), disabled: saving, children: TEAM_UNITS.map((option) => _jsx("option", { value: option.value, children: option.label }, option.value)) })] })] }), error && _jsx("span", { className: "add-team-error", children: error }), _jsxs("footer", { children: [_jsx("button", { type: "button", disabled: saving, onClick: resetAndClose, children: "Cancel" }), _jsxs("button", { type: "submit", disabled: saving, children: [saving ? _jsx(LoaderCircle, { className: "spin", size: 15 }) : null, "Create team"] })] })] })] }) }));
}
